import React, { useState, useEffect } from 'react';
import { CircularProgress, Grid, Box, Paper, TextField, Select, FormControl, InputLabel } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { omit } from 'lodash';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import useUser from '../lib/useUser';
import { apiFetch } from '../lib/fetch';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import IPClientAssets from '../components/ip-client-assets';
import { Button, TextArea } from '../lib/common';
import { NumericFormat } from 'react-number-format';
import { formatCurrency } from '../lib/util';
import { colors } from '../lib/styles';
import DeleteButton from '../components/delete-button';

// Custom NumericFormatCustom component for currency input
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
      decimalScale={2}
      fixedDecimalScale
    />
  );
});

const CustomTextField = (props) => {
  const { name, label, value, onChange, ...other } = props;

  return (
    <TextField
      {...other}
      name={name}
      label={label}
      value={value}
      onChange={onChange}
      style={{ ...styles.baseText, ...props.style }}
    />
  );
};    

const CurrencyTextField = (props) => {
  const { name, label, value, onChange, ...other } = props;
  return (
    <CustomTextField
      {...other}
      name={name}
      label={label}
      value={value}
      onChange={onChange}
      InputProps={{
        inputComponent: NumericFormatCustom,
        ...other.InputProps,
        inputProps: {
          ...other.InputProps?.inputProps,
          style: { ...other.InputProps?.inputProps?.style },
        },
      }}
    />
  );
};

const DateTextField = (props) => {
  const { name, label, value, onChange, ...other } = props;
  return (
    <TextField
      {...other}
      name={name}
      label={label}
      value={moment(value).format('YYYY-MM-DD')}
      onChange={onChange}
      type="date"
      style={styles.dobField}
    />
  );
};

const CustomSelectField = (props) => {
  const { id, name, label, value, onChange, children, ...other } = props;
  return (
  <FormControl fullWidth variant="outlined">
    <Select
      {...other}
      labelId={id}
      name={name}
      label={label}
      value={value || ''}
      onChange={onChange}
    >
      {children}
    </Select>
  </FormControl>
  )
}

const CustomClientField = (props) => {
  const { label, children, disabled, ...rest } = props;
  return (
    <Grid item {...rest}>
      <Box sx={{ 
        backgroundColor: '#4F5F4B', 
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px',
        color: colors.white,
        textAlign: 'center', 
        p: 2,
        fontWeight: 'bold',
        fontSize: { xs: '14px' },
        width: '100%',
        padding: '5px',
      }}>
        {label}
      </Box>
      <Box sx={{
        backgroundColor: disabled ? colors.lightGrey : colors.white,
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        border: `1px solid ${colors.primaryTextAccent}`,
        borderTop: 'none',
        p: 1,
        width: '100%',
      }}>
        {children}
      </Box>
    </Grid>
  )
}


const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
  },
  baseText: {
    color: colors.black,
    width: '100%',
  },
  dobField: {
    color: colors.black,
    width: '100%',
  },
  miscLabel: {
    color: colors.black,
    backgroundColor: '#fff',
    padding: '5px',
    fontWeight: 'bold',
    borderRadius: '4px',
    textAlign: 'left',
  },
}

const ages = Array(31).fill(0).map((_, i) => i + 50);

const ClientEdit = () => {
  const history = useHistory();
  const auth = useUser();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [hasSpouse, setHasSpouse] = useState(false);

  const allAssets = client?.assets || [];

  const assetsNQ = allAssets.filter((a) => a.asset_type === 'NON_QUALIFIED');
  const assetsTD = allAssets.filter((a) => a.asset_type === 'TAX_DEFFERED');
  const assetsTF = allAssets.filter((a) => a.asset_type === 'TAX_FREE');

  function addAsset(asset) {
    setClient({ ...client, assets: [...allAssets, asset] });
  }
  function updateAssets() {
    const newClient = { ...client, assets: [...allAssets] };
    setClient(newClient);
  }

  let yearsToRetirement = '';
  let spouseYearsToRetirement = '';
  let age = '';
  let spouseAge = '';
  if (client && client.dob) {
    const dob = moment(client.dob);
    age = moment().diff(dob, 'years');
    if (client.spouse_dob) {
      const spouseDob = moment(client.spouse_dob);
      spouseAge = moment().diff(spouseDob, 'years');
    }
  }
  if (age && client.retirement_age) {
    yearsToRetirement = client.retirement_age - age;
    if (yearsToRetirement < 0) {
      yearsToRetirement = 'Retired';
    }
  }
  if (spouseAge && client.spouse_retirement_age) {
    spouseYearsToRetirement = client.spouse_retirement_age - spouseAge;
    if (spouseYearsToRetirement < 0) {
      spouseYearsToRetirement = 'Retired';
    }
  }
  let homeEquityDisp = 0;
  if (client) {
    const heNum = (client.home_equity || 0) - (client.home_mortgage || 0);
    if (heNum < 0) {
      homeEquityDisp = '-' + formatCurrency(heNum);
    } else {
      homeEquityDisp = formatCurrency(heNum);
    }
  }

  let displayName = '';
  let totalRetAssets = 0;
  if (client) {
    if (client.lastname === client.spouse_lastname) {
      displayName = `${client.firstname} & ${client.spouse_firstname} ${client.lastname}`;
    }
    else if (client.spouse_firstname) {
      displayName = `${client.firstname} ${client.lastname} & ${client.spouse_firstname} ${client.spouse_lastname}`;
    } else {
      displayName = `${client.firstname} ${client.lastname}`;
    }
    client.assets.forEach((a) => {
      if (!a.toDel) {
        const val = Number(a.value) || 0;
        totalRetAssets += val;
      }
    });
    // totalRetAssets = client.assets.reduce((acc, a) => acc + (Number(a.value) || 0), 0);
  }

  useEffect(() => {
    if (!auth?.id) return;
    apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then((res) => {
      setClient(res);
      setHasSpouse(Boolean(res?.spouse_firstname));
    }).catch((e) => {
      let err = e.toString();
      setErr(err);
    });

  }, [auth?.id, id]);
  
  useEffect(() => {
    if (client) {
      setHasSpouse(Boolean(client.spouse_firstname));
    }
  }, [client?.spouse_firstname]);

  const handleSubmit = async () => {
    const toSave = { ...omit(client, [
      'id',
      'created_at',
      'updated_at',
      'agent_id',
      'agent_firstname',
      'agent_lastname',
      'agent_email',
      'agent_num',
    ]) };
    toSave.assets = toSave.assets.map((a) => {
      const as = omit(a, [
        'agent_id',
        'agent_firstname',
        'agent_lastname',
        'agent_email',
        'agent_num',
        'created_at',
        'updated_at',
      ]);
      as.value = Number(as.value) || 0;
      return as;
    });
    toSave.desired_ati = toSave.desired_ati || 0;
    toSave.current_income = toSave.current_income || 0;
    toSave.pension = toSave.pension || 0;
    toSave.rental = toSave.rental || 0;
    toSave.social_security = toSave.social_security || 0;
    toSave.ret_contributions = toSave.ret_contributions || 0;
    toSave.spouse_desired_ati = toSave.spouse_desired_ati || 0;
    toSave.spouse_current_income = toSave.spouse_current_income || 0;
    toSave.spouse_pension = toSave.spouse_pension || 0;
    toSave.spouse_rental = toSave.spouse_rental || 0;
    toSave.spouse_social_security = toSave.spouse_social_security || 0;
    toSave.spouse_ret_contributions = toSave.spouse_ret_contributions || 0;
    toSave.inflation_rate = toSave.inflation_rate || 2.8;
    toSave.desired_recovery = toSave.desired_recovery || totalRetAssets;
    toSave.effective_tax_rate = toSave.effective_tax_rate || 18.0;
    toSave.emergency_fund = toSave.emergency_fund || (totalRetAssets / 10);
    toSave.leg1_allocation = toSave.leg1_allocation || 65;
    toSave.leg2_allocation = toSave.leg2_allocation || 20;
    toSave.leg3_allocation = toSave.leg3_allocation || 15;
    toSave.leg1_age_to_begin = toSave.leg1_age_to_begin || age + 1;
    toSave.leg2_age_to_begin = toSave.leg2_age_to_begin || age + 6;
    toSave.leg3_age_to_begin = toSave.leg3_age_to_begin || age + 11;
    toSave.filing_status = toSave.filing_status || null;
    toSave.spouse_filing_status = toSave.spouse_filing_status || '';
    toSave.ltc = toSave.ltc || null;
    toSave.legacy = toSave.legacy || null;
    toSave.military = toSave.military || null;
    toSave.home_equity = toSave.home_equity || null;
    toSave.home_mortgage = toSave.home_mortgage || null;
    toSave.misc1 = toSave.misc1 || null;
    toSave.misc2 = toSave.misc2 || null;
    toSave.misc3 = toSave.misc3 || null;
    console.log('saving', toSave, auth.id, id);
    const saved = await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, toSave);
    setClient(saved);
    return saved;
  };

  const handleTextChange = (evt) => {
    console.log(evt);
    const { name, value } = evt.target;
    console.log(name, value);
    setClient({ ...client, [name]: value });
  };

  const handleDateChange = (evt) => {
    const { name, value } = evt.target;
    const formattedValue = moment(value).toISOString();
    setClient({ ...client, [name]: formattedValue });
  };

  const handleMaskEditChange = (evt) => {
    // Handle both legacy MaskInput format and new direct TextField format
    const { name, value } = evt.target ? evt.target : evt;
    setClient({ ...client, [name]: value });
  }

  const handleSave = () => {
    handleSubmit()
      .then((res) => {
        setClient({ ...res });
      })
      .catch((e) => {
        let err = e.toString();
        setErr(err);
      });
  }

  return (
    <IncomePlanWrapper handleSubmit={handleSave}>
      <Box style={styles.mainBody}>
        <div style={styles.errTxt}>{err?.toString()}</div>
        {client ? (
          <div style={{ margin: '10px' }}>
            <Grid container style={{ width: '100%' }}>
              <Box
                sx={{
                  flexGrow: 1,
                  maxWidth: '1100px',
                  margin: '15px',
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <CustomClientField label="First Name" xs={12} sm={6} md={4} lg={3}>
                    <CustomTextField
                        name="firstname"
                        value={client.firstname}
                        required
                        onChange={handleTextChange}
                      />
                  </CustomClientField>
                  <CustomClientField label="Last Name" xs={12} sm={6} md={4} lg={3}>
                     <CustomTextField
                      name="lastname"
                      value={client.lastname}
                      required
                      onChange={handleTextChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Date of Birth" xs={12} sm={6} md={4} lg={3}>
                     <DateTextField
                      value={client.dob}
                      name="dob"
                      onChange={handleDateChange}
                      sx={{ width: '100%' }}
                    />
                  </CustomClientField>
                  <CustomClientField label="Filing Status" xs={12} sm={6} md={4} lg={3}>
                     <CustomSelectField
                      value={client.filing_status || ''}
                      name="filing_status"
                      onChange={handleTextChange}
                    >
                      <MenuItem value="SINGLE">Single</MenuItem>
                      <MenuItem value="MFJ">MFJ</MenuItem>
                      <MenuItem value="HOH">HoH</MenuItem>
                    </CustomSelectField>
                  </CustomClientField>

                  <CustomClientField label="Retirement Age" xs={12} md={8} lg={3}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <div style={{width: '100%' }}>
                        <CustomSelectField
                          id="retirement-age-label"
                          value={client.retirement_age || ''}
                          name="retirement_age"
                          onChange={handleTextChange}
                        >
                          {ages.map((age) => (
                            <MenuItem key={age} value={age}>{age}</MenuItem>
                          ))}
                        </CustomSelectField>
                      </div>
                    </div>
                  </CustomClientField>

                  <CustomClientField label="Current Age" xs={12} md={8} lg={3}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <div style={{ width: '100%' }}>
                        <CustomTextField
                          name="age"
                          value={age}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </CustomClientField>

                  <CustomClientField label="Current Income" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="current_income"
                      value={client.current_income || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Desired Annual Target Income (ATI)" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="desired_ati"
                      value={client.desired_ati || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Pension" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="pension"
                      value={client.pension || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Pension Age" xs={12} md={8} lg={3}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <div style={{width: '100%' }}>
                        <CustomSelectField
                          id="pension-age-label"
                          value={client.pension_age}
                          name="pension_age"
                          onChange={handleTextChange}
                        >
                          <MenuItem key={0} value={null}> - </MenuItem>
                          {ages.map((age) => (
                            <MenuItem key={age} value={age}>{age}</MenuItem>
                          ))}
                        </CustomSelectField>
                      </div>
                    </div>
                  </CustomClientField>
                  <CustomClientField label="Rental" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="rental"
                      value={client.rental || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Ret Contributions" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="ret_contributions"
                      value={client.ret_contributions || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Social Security" xs={12} sm={6} md={4} lg={3}>
                    <CurrencyTextField
                      name="social_security"
                      value={client.social_security || ''}
                      onChange={handleMaskEditChange}
                    />
                  </CustomClientField>
                  <CustomClientField label="Social Security Age" xs={12} md={8} lg={3}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <div style={{width: '100%' }}>
                        <CustomSelectField
                          id="ss-age-label"
                          value={client.ss_age}
                          name="ss_age"
                          onChange={handleTextChange}
                        >
                          <MenuItem key={0} value={null}> - </MenuItem>
                          {ages.map((age) => (
                            <MenuItem key={age} value={age}>{age}</MenuItem>
                          ))}
                        </CustomSelectField>
                      </div>
                    </div>
                  </CustomClientField>
                </Grid>
              </Box>

              {!hasSpouse && <Button
                endIcon={<AddIcon/>}
                variant='link'
                sx={{
                  mx: 2,
                  color: colors.white,
                  backgroundColor: colors.primaryColor,
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    color: colors.white,
                    backgroundColor: colors.primaryAccent,
                  }
                }}
                onClick={() => {
                  setHasSpouse(true);
                }}
              >
                Add Spouse
              </Button>}

              {hasSpouse && 
                <>
                  <Box
                    sx={{
                      flexGrow: 1,
                      maxWidth: '1100px',
                      margin: '15px',
                      padding: '15px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Grid container spacing={2} sx={{ width: '100%' }}>
                      <CustomClientField label="Spouse First" xs={12} sm={6} md={4} lg={3}>
                        <CustomTextField
                            name="spouse_firstname"
                            value={client.spouse_firstname}
                            required
                            onChange={handleTextChange}
                          />
                      </CustomClientField>
                      <CustomClientField label="Spouse Last" xs={12} sm={6} md={4} lg={3}>
                        <CustomTextField
                          name="spouse_lastname"
                          value={client.spouse_lastname}
                          required
                          onChange={handleTextChange}
                        />
                      </CustomClientField>
                      <CustomClientField label="Spouse DOB" xs={12} sm={6} md={4} lg={3}>
                        <DateTextField
                          value={client.spouse_dob}
                          name="spouse_dob"
                          onChange={handleDateChange}
                        />
                      </CustomClientField>
                      {/* <CustomClientField disabled label="Filing Status" xs={12} sm={6} md={4} lg={3}>
                        <TextField
                            required
                            disabled
                            defaultValue={"N/A"}
                            variant="standard"
                          />
                      </CustomClientField> */}

                      <div/>

                      <CustomClientField label="Spouse Retirement Age" xs={12} md={8} lg={3}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <div style={{width: '100%' }}>
                            <CustomSelectField
                              variant="standard"
                              id="spouse-retirement-age-label"
                              label="Ret Age"
                              value={client.spouse_retirement_age || ''}
                              name="spouse_retirement_age"
                              onChange={handleTextChange}
                            >
                              {ages.map((age) => (
                                <MenuItem key={age} value={age}>{age}</MenuItem>
                              ))}
                            </CustomSelectField>
                          </div>
                        </div>
                      </CustomClientField>

                      <CustomClientField label="Spouse Current Age" xs={12} md={8} lg={3}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <CustomTextField
                            name="spouse_age"
                            value={spouseAge}
                            disabled={true}
                          />
                        </div>
                      </CustomClientField>

                      <CustomClientField label="Spouse Income" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          name="spouse_current_income"
                          value={client.spouse_current_income || ''}
                          onChange={handleMaskEditChange}
                        />
                      </CustomClientField>
                      {/* <CustomClientField disabled label="Desired ATI" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          disabled
                          name="desired_ati"
                          value={client.desired_ati || ''}
                        />
                      </CustomClientField> */}
                      <CustomClientField label="Spouse Pension" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          name="spouse_pension"
                          value={client.spouse_pension || ''}
                          onChange={handleMaskEditChange}
                        />
                      </CustomClientField>
                      <CustomClientField label="SpousePension Age" xs={12} md={8} lg={3}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <div style={{width: '100%' }}>
                            <CustomSelectField
                              id="spouse-pension-age-label"
                              value={client.spouse_pension_age}
                              name="spouse_pension_age"
                              onChange={handleTextChange}
                            >
                              <MenuItem key={0} value={null}> - </MenuItem>
                              {ages.map((age) => (
                                <MenuItem key={age} value={age}>{age}</MenuItem>
                              ))}
                            </CustomSelectField>
                          </div>
                        </div>
                      </CustomClientField>
                      <CustomClientField label="Spouse Rental" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          name="spouse_rental"
                          value={client.spouse_rental || ''}
                          onChange={handleMaskEditChange}
                        />
                      </CustomClientField>
                      <CustomClientField label="Spouse Ret Contrib" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          name="spouse_ret_contributions"
                          value={client.spouse_ret_contributions || ''}
                          onChange={handleMaskEditChange}
                        />
                      </CustomClientField>
                      <CustomClientField label="Spouse SS" xs={12} sm={6} md={4} lg={3}>
                        <CurrencyTextField
                          name="spouse_social_security"
                          value={client.spouse_social_security || ''}
                          onChange={handleMaskEditChange}
                        />
                      </CustomClientField>
                      <CustomClientField label="Spouse Social Security Age" xs={12} md={8} lg={3}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <div style={{width: '100%' }}>
                            <CustomSelectField
                              id="spouse-ss-age-label"
                              value={client.spouse_ss_age}
                              name="spouse_ss_age"
                              onChange={handleTextChange}
                            >
                              <MenuItem key={0} value={null}> - </MenuItem>
                              {ages.map((age) => (
                                <MenuItem key={age} value={age}>{age}</MenuItem>
                              ))}
                            </CustomSelectField>
                          </div>
                        </div>
                      </CustomClientField>
                    </Grid>
                  </Box>
                  <DeleteButton
                    iconStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#1d0', fontSize: '0.9rem', fontWeight: 'bold' }}
                    handleDelete={(d) => {
                      setHasSpouse(false);
                      setClient({ ...client, spouse_firstname: '', spouse_lastname: '', spouse_dob: '', spouse_retirement_age: '', spouse_filing_status: '', spouse_current_income: '', spouse_desired_ati: '', spouse_pension: '', spouse_rental: '', spouse_social_security: '', spouse_ret_contributions: '' });
                    }} 
                  />
                </>
              }
            </Grid>
            <Box id="tax-buckets" sx={{ flexGrow: 1, margin: {xs: '5px', sm: '10px', md: '15px'}, marginTop: '20px', marginBottom: '20px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <IPClientAssets
                    label="Non-Qualified"
                    type="NON_QUALIFIED"
                    assets={assetsNQ}
                    addAsset={addAsset}
                    updateAssets={updateAssets}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <IPClientAssets
                    label="Tax Deferred"
                    type="TAX_DEFFERED"
                    assets={assetsTD}
                    addAsset={addAsset}
                    updateAssets={updateAssets}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <IPClientAssets
                    label="Tax Free"
                    type="TAX_FREE"
                    assets={assetsTF}
                    addAsset={addAsset}
                    updateAssets={updateAssets}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box id="misc" sx={{ flexGrow: 1, margin: {xs: '5px', sm: '10px', md: '15px'}, marginTop: '20px', marginBottom: '20px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    backgroundColor: '#4F5F4B', 
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    color: colors.white,
                    textAlign: 'center', 
                    p: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    width: '100%',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    padding: '5px',
                  }}>
                    Misc
                  </Box>
                  <Box sx={{
                    backgroundColor: '#fff',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px',
                    color: colors.black,
                    textAlign: 'center', 
                    p: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    width: '100%',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                  }}>
                    <Grid container spacing={1}>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      <div>
                        LTC
                      </div>
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <TextField
                        name="ltc"
                        value={client.ltc || ''}
                        onChange={handleMaskEditChange}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      Legacy
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <TextField
                        name="legacy"
                        value={client.legacy || ''}
                        onChange={handleMaskEditChange}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      Military / 1st Resp
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <TextField
                        name="military"
                        value={client.military || ''}
                        onChange={handleMaskEditChange}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      Home Value
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <TextField
                        name="home_equity"
                        value={client.home_equity || ''}
                        onChange={handleMaskEditChange}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          style: { color: colors.black }
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      Home Mortgage
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <TextField
                        name="home_mortgage"
                        value={client.home_mortgage || ''}
                        onChange={handleMaskEditChange}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          style: { color: colors.black }
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} style={styles.miscLabel}>
                      Home Equity
                    </Grid>
                    <Grid item xs={6} sm={8} md={6}>
                      <div style={{ fontWeight: 'bold', textAlign: 'left' }}>
                        $ {homeEquityDisp}
                      </div>
                    </Grid>
                  </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ 
                      backgroundColor: '#4F5F4B', 
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      color: colors.white,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      width: '100%',
                      padding: '5px',
                    }}>
                      Total Retirement Assets
                    </Box>
                    <Box sx={{
                      backgroundColor: '#fff',
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: '8px',
                      color: colors.black,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width: '100%',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                      padding: '5px',
                    }}>
                      <Typography sx={{ color: colors.black, fontSize: '22px', margin: '10px' }}>$ {formatCurrency(totalRetAssets) || '0.00'}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ 
                      backgroundColor: '#4F5F4B', 
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      color: colors.white,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      width: '100%',
                      padding: '5px',
                    }}>
                      Notes
                    </Box>
                    <Box sx={{
                      backgroundColor: '#fff',
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: '8px',
                      color: colors.black,
                      textAlign: 'center', 
                      p: 2,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width: '100%',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    }}>
                      <TextArea
                        name="notes"
                        value={client.notes || ''}
                        onChange={handleTextChange}
                        fullWidth
                        rows={18}
                        style={{ 
                          maxHeight: '270px', // 18 rows to match Misc section
                          overflowY: 'auto',
                          color: colors.black,
                        }}
                      />
                    </Box>
                </Grid>
              </Grid>
            </Box>
          </div>
        ) : <CircularProgress />}
      </Box>
    </IncomePlanWrapper>
  );
};


export default ClientEdit;
